import { css } from "@emotion/css";
import { Button, ErrorPanel } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useState } from "react";
import { AddArgoCDInstanceDialog } from "~/areas/infrastructure/components/ArgoCDInstances/AddArgoCDInstanceDialog";
import { EmptyStateNoArgoCDApplications } from "~/areas/infrastructure/components/ArgoCDInstances/components/EmptyStateNoArgoCDApplications";
import { GenerateArgoCDAnnotationsDrawer } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/GenerateArgoCDAnnotationsDrawer";
import { useOptionalProjectContext } from "~/areas/projects/context/index";
import { ArgoCDDeploymentPreviewDrawer } from "~/components/Actions/argocd/ArgoCDDeploymentPreviewDrawer";
import ArgoImage from "~/components/Actions/argocd/ArgoIcon.svg";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import Summary from "~/components/form/Sections/Summary";
import type { ArgoCdInstancesAndApplications } from "./ArgoCDActionCommon";
import { isAnnotatedForProject } from "./ArgoCDActionCommon";
export function DeploymentPreviewExpander(props: {
    argoContext: InlineStatusQuery<ArgoCdInstancesAndApplications, null>;
    expandedByDefault?: boolean;
}) {
    const projectContext = useOptionalProjectContext();
    if (!projectContext) {
        return <></>;
    }
    return (<ExpandableFormSection errorKey="Octopus.Action.ArgoCD.DeploymentPreview" title="Deployment Preview" help="Preview which Argo applications this step will update with your current configuration." summary={Summary.summary("Preview which Argo applications this step will update with your current configuration.")} isExpandedByDefault={props.expandedByDefault}>
            <DeploymentPreviewPanel argoContext={props.argoContext} project={projectContext.state.model}/>
        </ExpandableFormSection>);
}
function DeploymentPreviewPanel(props: {
    argoContext: InlineStatusQuery<ArgoCdInstancesAndApplications, null>;
    project: ProjectResource;
}) {
    const { argoContext, project } = props;
    if (argoContext.isLoading && !argoContext.result) {
        return (<div className={styles.skeleton}>
                <Skeleton shape={"Rounded"} borderRadius={"small"}/>
            </div>);
    }
    if (argoContext.error) {
        const errorInfo = createErrorInfoFromError(argoContext.error);
        return <ErrorPanel error={errorInfo}/>;
    }
    if (argoContext.result) {
        if (argoContext.result.instances.Resources.length === 0) {
            return <CreateArgoCDGateway refetch={props.argoContext.refetch} spaceId={props.project.SpaceId}/>;
        }
        const projectMappedApps = argoContext.result.applications.filter((application) => isAnnotatedForProject(application, project.Slug));
        if (projectMappedApps.length === 0) {
            return <NoMappedApplications refetch={argoContext.refetch}/>;
        }
        else {
            return <DeploymentPreview project={project}/>;
        }
    }
    return <ErrorPanel error={{ message: "Failed to handle Argo instances and applications, deployment preview is unavailable.", details: [] }}/>;
}
function CreateArgoCDGateway(props: {
    spaceId: string;
    refetch: () => void;
}) {
    const [showAddDialog, setShowAddDialog] = useState(false);
    const handleDialogOnClose = (wasGatewayInstalled: boolean) => {
        setShowAddDialog(false);
        // if we installed a gateway, reload the gateway list
        if (wasGatewayInstalled) {
            props.refetch();
        }
    };
    return (<>
            <div className={styles.centringFlexbox}>
                <img src={ArgoImage} alt="ArgoCD Icon" style={{ width: 100, height: 100 }}/>
                <div className={styles.textBox}>
                    <div className={styles.headingLine}>You don't have any Argo CD instances to preview yet</div>
                    <div className={styles.descriptionLine}>Start by clicking on Add Argo CD Instance.</div>
                </div>
                <div className={styles.horizontalGroup}>
                    <Button importance={"primary"} label={"Add Argo CD Instance"} onClick={() => setShowAddDialog(true)} opensInNewTab={true}/>
                    <ExternalLink href={"./"}>Docs: Argo CD</ExternalLink>
                </div>
            </div>
            <AddArgoCDInstanceDialog open={showAddDialog} onClose={handleDialogOnClose} spaceId={props.spaceId}/>
        </>);
}
function NoMappedApplications(props: {
    refetch: () => void;
}) {
    const [showAnnotationsDrawer, setShowAnnotationsDrawer] = useState<boolean>(false);
    return (<>
            <EmptyStateNoArgoCDApplications onGenerateAnnotationsClick={() => {
            setShowAnnotationsDrawer(true);
        }}/>
            <GenerateArgoCDAnnotationsDrawer open={showAnnotationsDrawer} onClose={() => {
            props.refetch();
            setShowAnnotationsDrawer(false);
        }}/>
        </>);
}
function DeploymentPreview({ project }: {
    project: ProjectResource;
}) {
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const handleViewPreview = () => {
        setDrawerIsOpen(true);
    };
    const closePreviewDrawer = () => {
        setDrawerIsOpen(false);
    };
    return (<>
            <div className={styles.rightAlign}>
                <Button importance="secondary" label="Deployment Preview" onClick={() => handleViewPreview()}/>
            </div>
            <ArgoCDDeploymentPreviewDrawer isOpen={drawerIsOpen} onClose={() => closePreviewDrawer()} project={project}/>
        </>);
}
const styles = {
    centringFlexbox: css({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: space[32],
    }),
    textBox: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        marginBottom: space[24],
        alignItems: "center",
    }),
    headingLine: css({
        font: text.heading.small,
        alignContent: "center",
    }),
    descriptionLine: css({
        font: text.body.regular.medium,
        alignContent: "center",
    }),
    horizontalGroup: css({
        display: "flex",
        flexDirection: "row",
        width: "100%",
        gap: space[16],
        justifyContent: "center",
    }),
    rightAlign: css({
        display: "flex",
        justifyContent: "flex-end",
    }),
    skeleton: css({
        height: "24px",
    }),
};
